import type { LucideIcon } from "lucide-react";
import { Check, Pencil, Plus, X } from "lucide-react";
import { useState } from "react";
import type { HTMLAttributes, ReactNode } from "react";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { DECIMAL_INPUT_PATTERN, INTEGER_INPUT_PATTERN, normalizeNumericDraft } from "@/utils/numeric";

import { Spinner } from "./ui/spinner";

type CommitFn<TData> = (args: {
  rowId: string | number;
  columnId: string;
  value: unknown;
  row: TData;
}) => void | Promise<void>;

type DisplayAccessor<TData> =
  | keyof TData
  | ((row: TData) => ReactNode | string | number | null | undefined);

interface TextEditor<TData> {
  type: "text" | "number";
  placeholder?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
  getValue?: (row: TData) => string | number | null | undefined;
  onCommitTransform?: (value: string, row: TData) => unknown;
  allowDecimal?: boolean;
}

interface AsyncComboboxEditor<TData> {
  type: "async-combobox";
  placeholder?: string;
  loadOptions: (query: string, row: TData) => Promise<AsyncOption[]>;
  getCurrentOption?: (row: TData) => AsyncOption | null;
  buttonClassName?: string;
}

type EditableConfig<TData> = TextEditor<TData> | AsyncComboboxEditor<TData>;

interface EditableDataTableActionConfirm {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface EditableDataTableAction<TData> {
  id: string;
  label: string;
  icon?: LucideIcon;
  variant?: "ghost" | "outline" | "destructive" | "secondary";
  onSelect: (row: TData) => void | Promise<void>;
  confirm?: EditableDataTableActionConfirm;
  isDisabled?: (row: TData) => boolean;
  isHidden?: (row: TData) => boolean;
}

// Column definition keeps the API declarative so pages only describe rendering and editing.
export interface EditableDataTableColumn<TData> {
  id: string;
  header: ReactNode;
  accessor?: DisplayAccessor<TData>;
  renderCell?: (row: TData) => ReactNode;
  className?: string;
  editable?: EditableConfig<TData>;
  cellClassName?: string | ((row: TData) => string);
  truncate?: boolean | string; // true = responsive truncation, string = custom CSS class
}

// Parent component stays in control of persistence and decides when to show add-row actions.
interface EditableDataTableProps<TData> {
  columns: EditableDataTableColumn<TData>[];
  rows: TData[];
  getRowId: (row: TData) => string | number;
  onCommit: CommitFn<TData>;
  onCommitSuccess?: (args: { row: TData; columnId: string }) => void;
  onAddRow?: () => void;
  addRowLabel?: string;
  addRowIcon?: LucideIcon;
  errors?: Record<string, string | undefined>;
  isBusy?: boolean;
  emptyMessage?: string;
  className?: string;
  rowActions?: EditableDataTableAction<TData>[];
  actionsHeader?: ReactNode;
}

// Tracks the single cell currently in edit mode to avoid juggling multiple drafts at once.
interface EditingState {
  rowId: string | number;
  columnId: string;
}

interface DraftState {
  value: unknown;
  initialValue: unknown;
}

// Helper resolves either a key accessor or function accessor to keep the render logic tidy.
function extractAccessorValue<TData>(
  row: TData,
  accessor?: DisplayAccessor<TData>,
) {
  if (!accessor) return null;
  if (typeof accessor === "function") {
    return accessor(row);
  }
  return (row as Record<string, unknown>)[accessor as string];
}

function getDisplayValue<TData>(
  row: TData,
  column: EditableDataTableColumn<TData>,
) {
  if (column.renderCell) {
    return column.renderCell(row);
  }
  return extractAccessorValue(row, column.accessor);
}

function isPromise(value: unknown): value is Promise<unknown> {
  return (
    !!value &&
    typeof value === "object" &&
    "then" in (value as Record<string, unknown>)
  );
}

function areValuesEqual(a: unknown, b: unknown) {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

function getEditableValue<TData>(
  row: TData,
  column: EditableDataTableColumn<TData>,
) {
  const editable = column.editable;
  if (!editable) return null;

  if (editable.type === "async-combobox") {
    return editable.getCurrentOption?.(row) ?? null;
  }

  if (editable.getValue) {
    return editable.getValue(row);
  }

  return extractAccessorValue(row, column.accessor);
}

export function EditableDataTable<TData>({
  columns,
  rows,
  getRowId,
  onCommit,
  onCommitSuccess,
  onAddRow,
  addRowLabel = "Agregar fila",
  addRowIcon: AddIcon = Plus,
  errors = {},
  isBusy = false,
  emptyMessage = "No hay resultados",
  className,
  rowActions = [],
  actionsHeader,
}: EditableDataTableProps<TData>) {
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [pendingCell, setPendingCell] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{
    action: EditableDataTableAction<TData>;
    row: TData;
  } | null>(null);
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);

  const hasRowActions = rowActions.length > 0;

  const cellKey = (rowId: string | number, columnId: string) =>
    `${rowId}:${columnId}`;
  const getActionKey = (rowId: string | number, actionId: string) =>
    `${rowId}::${actionId}`;

  // Lazy-initialize drafts when the user focuses a cell.
  const beginEdit = (
    rowId: string | number,
    columnId: string,
    value: unknown,
  ) => {
    if (isBusy) return;
    setEditing({ rowId, columnId });
    setDrafts((prev) => ({
      ...prev,
      [cellKey(rowId, columnId)]: { value, initialValue: value },
    }));
  };

  // Drop the draft to fall back to server data on cancel.
  const cancelEdit = (rowId: string | number, columnId: string) => {
    setEditing((prev) =>
      prev && prev.rowId === rowId && prev.columnId === columnId ? null : prev,
    );
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[cellKey(rowId, columnId)];
      return next;
    });
  };

  // Normalize outgoing payloads and delegate real persistence to the caller.
  const commitEdit = async (
    row: TData,
    column: EditableDataTableColumn<TData>,
    valueOverride?: unknown,
  ) => {
    const rowId = getRowId(row);
    const columnId = column.id;
    const draft = drafts[cellKey(rowId, columnId)];
    if (!draft && valueOverride === undefined) return;

    const draftValue =
      valueOverride !== undefined
        ? valueOverride
        : (draft?.value ?? getEditableValue(row, column));

    let outgoingValue = draftValue;

    if (column.editable?.type !== "async-combobox") {
      const textCfg = column.editable!;
      const textValue = String(
        typeof draftValue === "string" || typeof draftValue === "number"
          ? draftValue
          : "",
      );
      outgoingValue = textCfg.onCommitTransform
        ? textCfg.onCommitTransform(textValue, row)
        : textValue;
    }

    const payload = {
      rowId,
      columnId,
      value: outgoingValue,
      row,
    };

    const result = onCommit(payload);
    if (isPromise(result)) {
      setPendingCell(cellKey(rowId, columnId));
      try {
        await result;
        cancelEdit(rowId, columnId);
        onCommitSuccess?.({ row, columnId });
      } finally {
        setPendingCell(null);
      }
    } else {
      cancelEdit(rowId, columnId);
      onCommitSuccess?.({ row, columnId });
    }
  };

  const setDraftValue = (
    rowId: string | number,
    columnId: string,
    value: unknown,
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [cellKey(rowId, columnId)]: {
        value,
        initialValue: prev[cellKey(rowId, columnId)]?.initialValue,
      },
    }));
  };

  const getDraftValue = (
    rowId: string | number,
    columnId: string,
  ): DraftState | undefined => drafts[cellKey(rowId, columnId)];

  const executeAction = async (
    action: EditableDataTableAction<TData>,
    row: TData,
  ) => {
    const rowId = getRowId(row);
    const key = getActionKey(rowId, action.id);
    setPendingActionKey(key);
    try {
      await Promise.resolve(action.onSelect(row));
      setConfirmState((current) => {
        if (!current) return null;
        const currentRowId = getRowId(current.row);
        if (current.action.id === action.id && currentRowId === rowId) {
          return null;
        }
        return current;
      });
    } finally {
      setPendingActionKey((current) => (current === key ? null : current));
    }
  };

  const handleActionClick = (
    action: EditableDataTableAction<TData>,
    row: TData,
  ) => {
    if (isBusy) return;
    if (action.isDisabled?.(row)) return;

    if (action.confirm) {
      setConfirmState({ action, row });
      return;
    }

    void executeAction(action, row);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      setConfirmState((current) => {
        if (!current) return null;
        const key = getActionKey(getRowId(current.row), current.action.id);
        if (pendingActionKey === key) {
          return current;
        }
        return null;
      });
    }
  };

  const getVisibleActions = (row: TData) =>
    rowActions.filter((action) => !action.isHidden?.(row));

  const getConfirmStateKey = () => {
    if (!confirmState) return null;
    return getActionKey(
      getRowId(confirmState.row),
      confirmState.action.id,
    );
  };

  const confirmStateKey = getConfirmStateKey();
  const isConfirmPending =
    confirmStateKey !== null && pendingActionKey === confirmStateKey;
  const confirmConfig = confirmState?.action.confirm;
  const confirmTitle =
    confirmConfig?.title ?? confirmState?.action.label ?? "Confirmar acción";
  const confirmDescription = confirmConfig?.description;
  const confirmCancelLabel = confirmConfig?.cancelLabel ?? "Cancelar";
  const confirmConfirmLabel = confirmConfig?.confirmLabel ?? "Confirmar";

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.id} className={column.className}>
                  {column.header}
                </TableHead>
              ))}
              {hasRowActions && (
                <TableHead className="w-0 text-right">
                  {actionsHeader}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (hasRowActions ? 1 : 0)}
                  className="h-20 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const rowId = getRowId(row);
                const visibleActions = getVisibleActions(row);

                return (
                  <TableRow key={rowId}>
                    {columns.map((column) => {
                      const columnId = column.id;
                      const columnError = errors[cellKey(rowId, columnId)];
                      const isEditing =
                        editing?.rowId === rowId &&
                        editing?.columnId === columnId;
                      const cellValue = getDisplayValue(row, column);
                      const cellClassName =
                        typeof column.cellClassName === "function"
                          ? column.cellClassName(row)
                          : column.cellClassName;

                      if (!column.editable) {
                        let displayValue: ReactNode = "-";
                        if (
                          typeof cellValue === "string" ||
                          typeof cellValue === "number" ||
                          typeof cellValue === "boolean" ||
                          cellValue === null ||
                          cellValue === undefined
                        ) {
                          displayValue = cellValue ?? "-";
                        }
                        return (
                          <TableCell
                            key={columnId}
                            className={cellClassName}
                          >
                            {displayValue}
                          </TableCell>
                        );
                      }

                      const editableConfig = column.editable;
                      const draft = getDraftValue(rowId, columnId);
                      const isPending =
                        pendingCell === cellKey(rowId, columnId);
                      const hasChanges =
                        draft &&
                        !areValuesEqual(draft.initialValue, draft.value);

                      const renderReadMode = () => {
                        const cellText = typeof cellValue === 'string' || typeof cellValue === 'number'
                          ? String(cellValue)
                          : '';
                        const shouldTruncate = column.truncate;
                        const truncateClass = typeof shouldTruncate === 'string'
                          ? shouldTruncate
                          : shouldTruncate
                            ? 'max-w-[200px]'
                            : '';

                        return (
                          <button
                            type="button"
                            className={cn(
                              "flex w-full items-center justify-between gap-2 rounded-md border border-transparent px-2 py-1 text-left transition-colors",
                              "hover:border-muted-foreground/20 hover:bg-muted/40 focus:outline-none focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-primary/20",
                              cellClassName,
                              isBusy && "cursor-not-allowed opacity-60",
                            )}
                            disabled={isBusy}
                            onClick={() =>
                              beginEdit(
                                rowId,
                                columnId,
                                getEditableValue(row, column),
                              )
                            }
                            title={shouldTruncate && cellText ? cellText : undefined}
                          >
                            <span className={cn("truncate", truncateClass)}>
                              {cellText || "-"}
                            </span>
                            <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          </button>
                        );
                      };

                      const renderTextEditor = (
                        cfg: TextEditor<TData>,
                        mode: "text" | "number",
                      ) => {
                        const baseValue =
                          draft?.value ??
                          cfg.getValue?.(row) ??
                          extractAccessorValue(row, column.accessor);
                        const stringValue = String(
                          typeof baseValue === "string" ||
                            typeof baseValue === "number"
                            ? baseValue
                            : "",
                        );

                        return (
                          <div className="flex flex-col gap-1">
                            <Input
                              value={stringValue}
                              placeholder={cfg.placeholder}
                              inputMode={cfg.inputMode}
                              type={mode === "number" ? "text" : "text"}
                              onChange={(event) => {
                                const raw = event.target.value;
                                if (mode === "number") {
                                  // Accept comma or dot while keeping the uncommitted text in local draft.
                                  const normalized = normalizeNumericDraft(raw);
                                  const allowDecimal = cfg.allowDecimal ?? true;
                                  const pattern = allowDecimal
                                    ? DECIMAL_INPUT_PATTERN
                                    : INTEGER_INPUT_PATTERN;
                                  if (!pattern.test(normalized)) {
                                    return;
                                  }
                                  setDraftValue(rowId, columnId, normalized);
                                } else {
                                  setDraftValue(rowId, columnId, raw);
                                }
                              }}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  void commitEdit(row, column);
                                }
                                if (event.key === "Escape") {
                                  event.preventDefault();
                                  cancelEdit(rowId, columnId);
                                }
                              }}
                              autoFocus
                              disabled={isPending}
                            />
                            <div className="flex items-center justify-center gap-1 mt-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => void commitEdit(row, column)}
                                disabled={isPending || !hasChanges}
                              >
                                {isPending ? (
                                  <Spinner className="h-4 w-4" />
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => cancelEdit(rowId, columnId)}
                                disabled={isPending}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            {columnError && (
                              <p className="text-xs text-destructive">
                                {columnError}
                              </p>
                            )}
                          </div>
                        );
                      };

                      const renderAsyncCombobox = (
                        cfg: AsyncComboboxEditor<TData>,
                      ) => {
                        // Editor stays uncontrolled: parent handles selection persistence in onCommit.
                        const currentOption =
                          (draft?.value as AsyncOption | null | undefined) ??
                          cfg.getCurrentOption?.(row) ??
                          null;

                        return (
                          <div className="flex flex-col gap-1">
                            <AsyncCombobox
                              value={currentOption}
                              onChange={(option) => {
                                setDraftValue(rowId, columnId, option);
                                void commitEdit(row, column, option);
                              }}
                              loadOptions={(query) =>
                                cfg.loadOptions(query, row)
                              }
                              placeholder={cfg.placeholder}
                              buttonClassName={cfg.buttonClassName}
                            />
                            {columnError && (
                              <p className="text-xs text-destructive">
                                {columnError}
                              </p>
                            )}
                          </div>
                        );
                      };

                      return (
                        <TableCell
                          key={columnId}
                          className={cn("align-top", cellClassName)}
                        >
                          {isEditing ? (
                            editableConfig.type === "async-combobox" ? (
                              renderAsyncCombobox(editableConfig)
                            ) : (
                              renderTextEditor(
                                editableConfig,
                                editableConfig.type,
                              )
                            )
                          ) : (
                            <>
                              {renderReadMode()}
                              {columnError && (
                                <p className="mt-1 text-xs text-destructive">
                                  {columnError}
                                </p>
                              )}
                            </>
                          )}
                        </TableCell>
                      );
                    })}
                    {hasRowActions && (
                      <TableCell className="align-top">
                        {visibleActions.length > 0 ? (
                          <div className="flex justify-end gap-2">
                            {visibleActions.map((action) => {
                              const Icon = action.icon;
                              const key = getActionKey(rowId, action.id);
                              const isActionPending = pendingActionKey === key;
                              const isActionDisabled =
                                isBusy ||
                                (action.isDisabled?.(row) ?? false) ||
                                isActionPending;

                              return (
                                <Button
                                  key={action.id}
                                  type="button"
                                  variant={action.variant ?? "ghost"}
                                  size="sm"
                                  className={cn(
                                    "gap-2",
                                    action.variant === "ghost" && "px-2",
                                  )}
                                  onClick={() => handleActionClick(action, row)}
                                  disabled={isActionDisabled}
                                >
                                  {isActionPending ? (
                                    <Spinner className="h-4 w-4" />
                                  ) : (
                                    Icon && (
                                      <Icon className="h-4 w-4 shrink-0" />
                                    )
                                  )}
                                  <span className="whitespace-nowrap">
                                    {action.label}
                                  </span>
                                </Button>
                              );
                            })}
                          </div>
                        ) : null}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {onAddRow && (
        <div className="flex justify-start">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onAddRow()}
            disabled={isBusy}
            className="gap-2"
          >
            <AddIcon className="h-4 w-4" />
            {addRowLabel}
          </Button>
        </div>
      )}
      <Dialog open={Boolean(confirmState)} onOpenChange={handleDialogOpenChange}>
        {confirmState && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{confirmTitle}</DialogTitle>
              {confirmDescription && (
                <DialogDescription>{confirmDescription}</DialogDescription>
              )}
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmState(null)}
                disabled={isConfirmPending}
              >
                {confirmCancelLabel}
              </Button>
              <Button
                type="button"
                variant={
                  confirmState.action.variant === "destructive"
                    ? "destructive"
                    : "default"
                }
                onClick={() => void executeAction(confirmState.action, confirmState.row)}
                disabled={isConfirmPending}
                className="gap-2"
              >
                {isConfirmPending && <Spinner className="h-4 w-4" />}
                {confirmConfirmLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
